import useUserCanEditProject from '@API/project/useUserCanEditProject';
import {
  DeleteViewModal,
  FormViewModal,
  ViewSelect
} from '@shared/components/ViewSelect';
import React from 'react';
import useExpDetailsViewSelectOptions from './hooks/useExpDetailsViewSelectOptions';
import useExpDetailsViewDelete from './hooks/useExpDetailsViewDelete';
import useExpDetailsViewUpsert from './hooks/useExpDetailsViewUpsert';
import useExpDetailsPersonalViewOption from './hooks/useExpDetailsPersonalViewOption';
import useExpDetailsAutosaveLocalView from './hooks/useExpDetailsAutosaveLocalView';
import { useIsUserLoggedIn } from '@shared/hooks';
import useExpDetailsCheckViewsUpdates from './hooks/useExpDetailsCheckViewsUpdates';
import useExpDetailsSelectedTemplateId from '@experiment-details/hooks/useExpDetailsSelectedTemplateId';
import { AUTO_GENERATED_OPTION_ID } from '@shared/constants';

const ExperimentDetailsViewSelect: React.FC = () => {
  const userCanEditProject = useUserCanEditProject();
  const savedTemplateId = useExpDetailsSelectedTemplateId();
  const isUserLoggedIn = useIsUserLoggedIn();

  const {
    projectViewList,
    workspaceViewList
  } = useExpDetailsViewSelectOptions();

  const {
    onSetAsDefaultView,
    onSelectView,
    onEditTemplate,
    onDuplicateTemplate,
    onCreateNewTemplate,
    actionModalConfig
  } = useExpDetailsViewUpsert();
  const { onDeleteTemplate, deleteModalConfig } = useExpDetailsViewDelete();

  useExpDetailsAutosaveLocalView();
  useExpDetailsCheckViewsUpdates();

  const {
    defaultView,
    onRegenerateDefaultView
  } = useExpDetailsPersonalViewOption();

  const selectedViewId = savedTemplateId || AUTO_GENERATED_OPTION_ID;

  const optionList = [
    {
      title: 'Views By Comet',
      itemList: [defaultView]
    },
    {
      title: 'Single experiment views',
      itemList: projectViewList
    },
    {
      title: 'workspace views',
      infoText:
        'Browse single experiment views from all projects within this workspace. Any changes will be saved to a duplicated view within this project.',
      isExpanded: workspaceViewList.some(
        option => option.isDefault || option.id === selectedViewId
      ),
      itemList: workspaceViewList
    }
  ];

  return (
    <>
      <ViewSelect
        optionList={optionList}
        selectedViewId={selectedViewId}
        onSelectView={onSelectView}
        onCreateNewView={onCreateNewTemplate}
        onEditView={onEditTemplate}
        onDuplicateView={onDuplicateTemplate}
        onSetDefaultView={onSetAsDefaultView}
        onDeleteView={onDeleteTemplate}
        onRegenerateDefaultView={onRegenerateDefaultView}
        indicatorLabel="Syncing is paused and new panels will not be shown. Regenerating the view updates panels and discards manual edits. You can save your changes by duplicating the view."
        actionsDisabled={!userCanEditProject}
        disabled={!isUserLoggedIn}
      />

      <DeleteViewModal {...deleteModalConfig} />
      <FormViewModal {...actionModalConfig} />
    </>
  );
};

export default ExperimentDetailsViewSelect;
